import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Document,
  Chapter,
  MoreHorizontal,
  Plus,
  Pencil,
  Options,
  Library,
  Play,
  Image,
  ImageFilled,
  Documents,
  Ai,
} from '@teo/components/icons';
import { IconButton, Dropdown } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import { newUid } from '@/util';
import { uploadLesson, uploadImage } from '@/query/documents';
import { LessonSettingsMoadel } from './LessonSettings/LessonSettingsMoadel';
import { uploadVideo } from '@/query/vimeo';
import { LibraryModal } from '../LibraryModal/LibraryModal';
import DeleteModalLesson from '../DeleteModal/DeleteModalLesson';
import Hint from '../Hints/Hints';
import { VideoUploadModal } from '../VideoUploadModal/VideoUploadModal';
import { encode } from '@/base64';
import { Video } from '@/components/Froala/widgets/ChatGPT/Icon/index';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { AiDocumentJobModal } from '@/components/AiDocumentJobModal';

export const CustomNodeAction = (props: any) => {
  const {
    node,
    newTree,
    coursecontentId,
    setAddLesson,
    setAddFolder,
    handleEditClick,
    handleDelete,
    setTree,
    setNewName,
    lessons,
    setLessons,
    setDirectories,
    isMobile,
    onRefresh,
    handleOpenLibrary,
    setUpdateLesInfo,
    setAddNumber,
    addNumber,
    setLoadVersion,
    loadVersion,
    setNewActiveNode,
    setIsOpenImageLibrary,
    isOpenImageLibrary,
    setIsOpenVideoModal,
    isOpenVideoModal,
    editName,
    itemRef,
  } = props;

  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser: any = auth?.user;

  const fileInputRef = useRef<any>(null);

  const { droppable, data } = node;
  const [newLesson, setNewLesson] = useState<any>();
  const [newFolder, setNewFolder] = useState<any>();
  const [newAsset, setNewAsset] = useState<any>();
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAiModifyModal, setIsOpenAiModifyModal] = useState(false);
  const [isItemBottom, setIsItemBottom] = useState(false);

  const handleAiModificationCompleted = (result: any) => {
    const data =
      result?.result || result?.data?.result || result?.data || result;
    const lessonId = Number(data?.lessonId || node?.data?.lessonId || 0);
    const name = data?.title || data?.name || node?.data?.name;

    if (lessonId && name) {
      setNewName(name);
      setLessons?.((currentLessons: any[]) =>
        currentLessons?.map((lesson: any) =>
          Number(lesson?.lessonId) === lessonId ? { ...lesson, name } : lesson
        )
      );
      window.dispatchEvent(
        new CustomEvent('teo:lessonRenamed', {
          detail: { lessonId, name },
        })
      );
    }

    onRefresh?.();
  };

  const handleNewLesson = async (
    name: string = t('pages.create_edit_course.new_lesson_button'),
    content = '',
    lessonType = 'theory'
  ) => {
    try {
      const { data: lesson } = await axios.post(`${config.backend}/lessons`, {
        name: name,
        canEdit: true,
        description: '',
        lessonType: lessonType,
      });
      // Store the content alongside the lesson so the effect can upload it
      setNewLesson({ ...lesson, _pendingContent: content });

      return lesson.id as number;
    } catch (err) {
      console.error('🔥 Failed to create lesson:', err);
      throw err;
    }

    // const data = await axios.post(`${config.backend}/lessons`, {
    //   name: t('pages.create_edit_course.new_lesson_button'),
    //   canEdit: true,
    //   content: '',
    //   description: '',
    //    lessonType: lessonType,
    // });
    // setNewLesson(data?.data);
  };

  const createLesson = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      canEdit: true,
      coursecontentId: parseInt(coursecontentId, 10),
      lessonId: newLesson?.id,
    });

    const createLessonId = result?.data?.lessonId;
    const createExtraInfoUsesId = result?.data?.id;

    if (createLessonId) {
      let newYindex;
      const parentTree: any = newTree.filter(
        (item: any) => item?.parent === node?.id
      );
      if (parentTree?.length) {
        newYindex =
          parentTree?.reduce((max: any, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, parentTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/use/${result?.data?.id}`, {
        coursecontentId: parseInt(coursecontentId, 10),
        lessonId: createLessonId,
        path: `${data?.path}/${createLessonId}`,
        yindex: newYindex,
      });
      setAddLesson({
        ...newLesson,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        numbered: true,
        lessonId: createLessonId,
        path: `${data?.path}/${createLessonId}`,
        usesId: createExtraInfoUsesId,
        yindex: newYindex,
      });
      setNewActiveNode(createExtraInfoUsesId);
      setTree(null);
    }
  };

  useEffect(() => {
    if (!newLesson?.id) return;

    const content = newLesson._pendingContent ?? '';

    uploadLesson(newLesson.postData, content)
      .then(() => {
        createLesson();
      })
      .catch((err: unknown) => {
        console.error('❌ Upload failed for lesson', newLesson.id, err);
      });
  }, [newLesson]);

  const quickInsertClick = () => {
    const itemTree = itemRef.current;
    const contentTree = document.getElementById('course-lessons-list');
    const blockTree = document.getElementById('course-lessons');
    if (itemTree && contentTree && blockTree) {
      const positionItem = itemTree.offsetTop;
      const contentTreeHeight = contentTree.offsetHeight;
      const blockTreeHeight = blockTree.offsetHeight;

      const parentHeight =
        blockTreeHeight > contentTreeHeight
          ? blockTreeHeight
          : contentTreeHeight;

      if (positionItem + 350 > parentHeight) {
        setIsItemBottom(true);
      } else {
        setIsItemBottom(false);
      }
    }
  };

  useEffect(() => {
    quickInsertClick();
  }, [newTree]);

  const createFolder = async () => {
    const data = await axios.post(`${config.backend}/directories`, {
      coursecontentId: parseInt(coursecontentId, 10),
      name: t('pages.create_edit_course.new_chapter_button'),
    });
    setNewFolder(data?.data);
  };

  useEffect(() => {
    if (newFolder?.id) {
      const newPath = `${newUid(20)}`;
      let newYindex;
      const parentTree: any = newTree.filter(
        (item: any) => item?.parent === node?.id
      );
      if (parentTree?.length) {
        newYindex =
          parentTree?.reduce((max: any, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, parentTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/directory/${newFolder?.id}`, {
        coursecontentId: parseInt(coursecontentId, 10),
        id: newFolder?.id,
        name: t('pages.create_edit_course.new_folder'),
        path: `${data?.path}/${newPath}`,
        yindex: newYindex,
      });
      setAddFolder({
        ...newFolder,
        coursecontentId: parseInt(coursecontentId, 10),
        id: newFolder?.id,
        numbered: true,
        name: t('pages.create_edit_course.new_folder'),
        path: `${data?.path}/${newPath}`,
        yindex: newYindex,
      });
      setNewActiveNode(newFolder?.id);
      setTree(null);
    }
  }, [newFolder?.id]);

  const handleUploadImage = async (event: any) => {
    const img = event.target.files[0];
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUploadImageLibrary = async (image: any) => {
    const res = await fetch(image);
    const blob = await res.blob();
    const mime = blob.type;
    const ext = mime.slice(mime.lastIndexOf('/') + 1, mime.length);
    const img = new File([blob], `${blob.size}.${ext}`, {
      type: mime,
    });
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUseImageLibrary = async (imageUrl: any) => {
    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: imageUrl,
    });
    setNewAsset(dataImage?.data);
  };

  useEffect(() => {
    if (newAsset) {
      createImageInfo();
    }
  }, [newAsset]);

  const createImageInfo = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      coursecontentId: parseInt(coursecontentId, 10),
      assetId: newAsset?.id,
    });

    const createAssetId = result?.data?.assetId;
    const createAssetUsesId = result?.data?.id;

    if (createAssetId) {
      let newYindex: any;
      const parentTree: any = newTree.filter(
        (item: any) => item?.parent === node?.id
      );
      if (parentTree?.length) {
        newYindex =
          parentTree?.reduce((max: any, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, parentTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/use/${result?.data?.id}`, {
        assetId: createAssetId,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        onlyTheory: true,
        path: `${data?.path}/${createAssetId}`,
        yindex: newYindex,
      });
      setAddLesson({
        ...newAsset,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        onlyTheory: true,
        numbered: true,
        assetId: createAssetId,
        path: `${data?.path}/${createAssetId}`,
        usesId: createAssetUsesId,
        yindex: newYindex,
      });
      setNewActiveNode(createAssetUsesId);
      setTree(null);
      setNewAsset(null);
    }
  };

  useEffect(() => {
    if (isOpenSettings || isOpenImageLibrary) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpenSettings, isOpenImageLibrary]);

  const handleStateLesson = async (state: any) => {
    const content = `<div style="position:relative" contenteditable=false class="widget-placeholder fr-deletable" id="${
      state.uid
    }"><div data-open="SOW" data-state="${encode(
      JSON.stringify(state)
    )}"><div data-closed="EOW"></div></div></div>`;

    const title =
      state.title ||
      t('pages.create_edit_course.new_lesson_button') ||
      'New lesson';
    return handleNewLesson(title, content, 'instruction');
  };

  function startInstruction() {
    const widgetState = {
      uid: newUid(10),
      type: 'Instruction',
      titleWidget: 'Instruction',
      noSubmit: true,
      url: null,
      title: t('pages.create_edit_course.video_instruction.new_instructional'),
      statusUrl: null,
      stepsUrl: null,
      steps: [],
    };

    handleStateLesson(widgetState);
  }

  const duplicateLesson = async () => {
    if (!node?.data?.lessonId) return;

    const currentYindex = Number(node?.data?.yindex || 0);
    const newYindex = currentYindex + 1;

    const siblingsToMove = newTree.filter(
      (item: any) =>
        item.parent === node.parent &&
        item.id !== node.id &&
        Number(item?.data?.yindex || 0) > currentYindex
    );

    for (const sibling of siblingsToMove) {
      const nextYindex = Number(sibling.data.yindex || 0) + 1;

      if (sibling.droppable) {
        await axios.put(`${config.backend}/directory/${sibling.data.id}`, {
          yindex: nextYindex,
        });

        setDirectories((prev: any) =>
          prev?.map((dir: any) =>
            dir.id === sibling.data.id ? { ...dir, yindex: nextYindex } : dir
          )
        );
      } else {
        // lesson / asset use
        await axios.put(`${config.backend}/use/${sibling.data.id}`, {
          yindex: nextYindex,
        });

        setLessons((prev: any) =>
          prev?.map((les: any) =>
            les.usesId === sibling.data.usesId
              ? { ...les, yindex: nextYindex }
              : les
          )
        );
      }
    }

    const { data: sourceLesson } = await axios.get(
      `${config.backend}/lesson/${node.data.lessonId}?write_access=true`
    );

    const { data: copiedLesson } = await axios.post(
      `${config.backend}/lessons`,
      {
        name: sourceLesson.name || node.data.name,
        canEdit: true,
        content: '',
        description: sourceLesson.description || '',
        lessonType: sourceLesson.lessonType || node.data.lessonType || 'theory',
      }
    );

    let sourceContent = '';

    if (sourceLesson.getData) {
      const res = await fetch(sourceLesson.getData);

      if (!res.ok) {
        throw new Error('Failed to load source lesson content');
      }

      sourceContent = await res.text();
    }

    await uploadLesson(copiedLesson.postData, sourceContent);

    const { data: copiedUse } = await axios.post(`${config.backend}/uses`, {
      canEdit: true,
      coursecontentId: parseInt(coursecontentId, 10),
      lessonId: copiedLesson.id,
    });

    const parentPath = String(node.data.path || '')
      .split('/')
      .slice(0, -1)
      .join('/');
    const newPath = parentPath
      ? `${parentPath}/${copiedLesson.id}`
      : `/${copiedLesson.id}`;

    await axios.put(`${config.backend}/use/${copiedUse.id}`, {
      coursecontentId: parseInt(coursecontentId, 10),
      lessonId: copiedLesson.id,
      path: newPath,
      yindex: newYindex,
    });

    setAddLesson({
      ...sourceLesson,
      ...copiedLesson,
      canEdit: true,
      coursecontentId: parseInt(coursecontentId, 10),
      id: copiedLesson.id,
      numbered: node.data.numbered,
      lessonId: copiedLesson.id,
      path: newPath,
      usesId: copiedUse.id,
      yindex: newYindex,
      postData: copiedLesson.postData,
      getData: copiedLesson.getData,
    });

    setNewActiveNode(copiedUse.id);
    // setTree(null);
  };

  return (
    <div className="">
      {droppable && (
        <div
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          className="relative inline-block"
        >
          {!editName && (
            <Dropdown>
              <Dropdown.Trigger>
                <IconButton
                  Icon={Plus}
                  size="sm"
                  variant="ghost"
                  className="w-5 min-w-5 text-[#7d7d81]"
                />
              </Dropdown.Trigger>
              <Dropdown.Content
                style={
                  !isMobile
                    ? {
                        visibility: 'visible',
                        right: '0',
                        top: isItemBottom ? 'auto' : '20px',
                        bottom: isItemBottom ? 'calc(100% + 10px)' : 'auto',
                        position: 'absolute',
                      }
                    : {
                        visibility: 'visible',
                        right: '0',
                        top: isItemBottom ? 'auto' : '20px',
                        bottom: isItemBottom ? 'calc(100% + 10px)' : 'auto',
                        position: 'absolute',
                      }
                }
              >
                <Dropdown.Actions>
                  <Dropdown.Button onClick={() => handleNewLesson()}>
                    <Document className="m-2 mr-4 h-5 w-5 text-grey-07" />
                    <p>{t('pages.create_edit_course.new_lesson_button')}</p>
                  </Dropdown.Button>

                  <Dropdown.Button onClick={() => createFolder()}>
                    <Chapter className="m-2 mr-4 h-5 w-5 text-grey-07" />
                    <p>{t('pages.create_edit_course.new_chapter_button')}</p>
                  </Dropdown.Button>

                  <Dropdown.Button
                    onClick={() => {
                      setIsOpenImageLibrary(true);
                    }}
                  >
                    <Image className="m-2 mr-4 h-5 w-5 text-grey-07" />
                    <p>{t('extra_info.upload_img')}</p>
                  </Dropdown.Button>

                  <Dropdown.Button onClick={() => setIsOpenVideoModal(true)}>
                    <Play className="m-2 mr-4 h-5 w-5 text-grey-07" />
                    <p>{t('extra_info.upload_video')}</p>
                  </Dropdown.Button>

                  <Dropdown.Button
                    onClick={() => {
                      handleOpenLibrary(data?.path, data?.id);
                    }}
                  >
                    <Library className="m-2 mr-4 h-5 w-5 text-tint-blue" />
                    <p>{t('pages.create_edit_course.from_library_button')}</p>
                  </Dropdown.Button>
                  <Dropdown.Button
                    onClick={() => {
                      startInstruction();
                    }}
                  >
                    <Video className={`m-2 mr-4 h-5 w-5 text-grey-07`} />

                    <p>
                      {t(
                        'pages.create_edit_course.video_instruction.instructional_video'
                      )}
                    </p>
                  </Dropdown.Button>
                </Dropdown.Actions>
              </Dropdown.Content>
            </Dropdown>
          )}
        </div>
      )}

      {!editName && (
        <div
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          className="relative inline-block"
        >
          <Dropdown>
            <Dropdown.Trigger>
              {/* {node?.parent === 0 && node?.data?.order === 1 && (
                <Hint
                  key={`hint_41`}
                  id={'hint_41'}
                  leftDots={'-left-2 top-0.5'}
                  title={t('hint.title_41')}
                  order={41}
                  style={{
                    left: '2px',
                    bottom: '70px',
                  }}
                />
              )} */}
              <IconButton
                className="w-5 min-w-5 text-[#7d7d81]"
                Icon={MoreHorizontal}
                variant="ghost"
                size="sm"
              />
            </Dropdown.Trigger>
            <Dropdown.Content
              style={
                !isMobile
                  ? {
                      visibility: 'visible',
                      right: '0',
                      top: isItemBottom ? 'auto' : '20px',
                      bottom: isItemBottom ? 'calc(100% + 10px)' : 'auto',
                      position: 'absolute',
                    }
                  : {
                      visibility: 'visible',
                      right: '0',
                      top: isItemBottom ? 'auto' : '20px',
                      bottom: isItemBottom ? 'calc(100% + 10px)' : 'auto',
                      position: 'absolute',
                    }
              }
            >
              <Dropdown.Actions>
                {/* {!droppable && (node.data.lessonId || node.data.assetId) && ( */}
                <Dropdown.Button
                  onClick={() => {
                    setIsOpenSettings(true);
                  }}
                >
                  <Options className="mr-4 w-5 text-[#231f20]" />
                  <p>{t('pages.create_edit_course.settings')}</p>
                </Dropdown.Button>
                {/* )} */}
                {(node.data.canEdit || !node.data.lessonId) && ( //directories and non linked lessons
                  <Dropdown.Button onClick={() => handleEditClick()}>
                    <Pencil className="mr-4 w-5 text-[#231f20]" />
                    <p>{t('pages.create_edit_course.rename')}</p>
                  </Dropdown.Button>
                )}

                {node.data.lessonId && (
                  <Dropdown.Button onClick={duplicateLesson}>
                    <Documents className="mr-4 w-5 text-[#231f20]" />
                    <p>{t('pages.create_edit_course.duplicate_button')}</p>
                  </Dropdown.Button>
                )}

                {authUser?.useTeoAiCourseAndLessonGeneration &&
                  node.data.lessonId &&
                  node.data.canEdit && (
                    <Dropdown.Button
                      onClick={() => setIsOpenAiModifyModal(true)}
                    >
                      <Ai className="mr-4 h-5 w-5 text-tint-blue" />
                      <p>
                        {t(
                          'pages.ai_document_job_modal.modify_with_ai',
                          'Modify with AI'
                        )}
                      </p>
                    </Dropdown.Button>
                  )}

                <Dropdown.Button onClick={() => setIsOpenDeleteModal(true)}>
                  <img
                    src="/images/icons/trash_icon_red.svg"
                    alt="delete button"
                    className="mr-5 w-4"
                  />
                  <p>{t('pages.create_edit_course.remove')}</p>
                </Dropdown.Button>
              </Dropdown.Actions>
            </Dropdown.Content>
          </Dropdown>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleUploadImage}
        accept="image/*"
        style={{ display: 'none' }}
        multiple={false}
      />
      {isOpenSettings && (
        <LessonSettingsMoadel
          onRefresh={onRefresh}
          coursecontentId={coursecontentId}
          closeModal={() => setIsOpenSettings(false)}
          les={node?.data}
          setIsOpenSettings={setIsOpenSettings}
          setLessons={setLessons}
          setDirectories={setDirectories}
          setNewName={setNewName}
          setUpdateLesInfo={setUpdateLesInfo}
          setAddNumber={setAddNumber}
          addNumber={addNumber}
          setLoadVersion={setLoadVersion}
          loadVersion={loadVersion}
        />
      )}
      {isOpenVideoModal && (
        <VideoUploadModal
          setIsOpenVideoModal={setIsOpenVideoModal}
          isOpenVideoModal={isOpenVideoModal}
          setNewAsset={setNewAsset}
        />
      )}
      {isOpenImageLibrary && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenImageLibrary}
          isOpenLibraryModal={isOpenImageLibrary}
          lessonPage={false}
          setUploadFile={handleUploadImageLibrary}
          onSelectImage={handleUseImageLibrary}
        />
      )}
      {isOpenDeleteModal && (
        <DeleteModalLesson
          title={
            node?.data?.lessonId
              ? t('pages.create_edit_course.delete_modal.title_les')
              : node?.data?.assetId
              ? t('pages.create_edit_course.delete_modal.title_asset')
              : t('pages.create_edit_course.delete_modal.title_chapter')
          }
          heading={
            node?.data?.lessonId
              ? t('pages.create_edit_course.delete_modal.heading_les')
              : node?.data?.assetId
              ? t('pages.create_edit_course.delete_modal.heading_asset')
              : t('pages.create_edit_course.delete_modal.heading_chapter')
          }
          text={
            node?.data?.lessonId
              ? t('pages.create_edit_course.delete_modal.subtitle_les')
              : node?.data?.assetId
              ? t('pages.create_edit_course.delete_modal.subtitle_assey')
              : t('pages.create_edit_course.delete_modal.subtitle_chapter')
          }
          les={node?.data}
          setIsOpenDeleteModal={setIsOpenDeleteModal}
          closeModal={() => setIsOpenDeleteModal(false)}
          handleDelete={handleDelete}
        />
      )}
      {authUser?.useTeoAiCourseAndLessonGeneration &&
        node?.data?.lessonId &&
        node?.data?.canEdit && (
          <AiDocumentJobModal
            open={isOpenAiModifyModal}
            mode="lesson"
            coursecontentId={coursecontentId}
            endpoint={`${config.backend}/external/v1/instructai/documents/lesson/${node.data.lessonId}/modify/start`}
            extraPayload={{
              lessonId: Number(node.data.lessonId),
              usesId: node.data.usesId || node.data.id || null,
              path: node.data.path || null,
              yindex: node.data.yindex ?? null,
              ...(node.data.lang ? { lang: node.data.lang } : {}),
            }}
            initialTitle={node.data.name}
            examplePrompts={[]}
            onClose={() => setIsOpenAiModifyModal(false)}
            onCompleted={handleAiModificationCompleted}
            title={t(
              'pages.ai_document_job_modal.modify_lesson_title',
              'Modify lesson with AI'
            )}
            description={t(
              'pages.ai_document_job_modal.modify_lesson_description',
              'Describe what you want AI to change in this lesson.'
            )}
            promptPlaceholder={t(
              'pages.ai_document_job_modal.modify_lesson_prompt_placeholder',
              'Describe the changes you want to make. Documents are optional.'
            )}
            startLabel={t(
              'pages.ai_document_job_modal.start_modifying',
              'Start modifying'
            )}
          />
        )}
    </div>
  );
};
